// src/components/admin/products/tabs/MarketplaceProducts.tsx
// Editor de conteúdo dos cards do Marketplace:
// - Acesso Total: descrição longa + bullets de features
// - Combos & Ciclos: descrição exibida no card full-width
// Lê produtos existentes e salva campos extras via API

import React, { useState, useMemo } from 'react';
import { Save, Globe, Layers, GraduationCap, RefreshCw, Plus, X } from 'lucide-react';
import './MarketplaceProducts.css';
import { useAdminProducts, useInvalidate } from '../../../../hooks/useAdminQueries';
import { Product } from '../../../../types/marketplace.types';

interface MarketplaceProductsProps {
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

type EditSection = 'access_all' | 'combos';

// Features padrão para Acesso Total (editável por produto)
const DEFAULT_FEATURES = [
    'Todos os decks e atualizações futuras',
    'Todos os ciclos de estudo inclusos',
    'Novos conteúdos adicionados automaticamente',
    'Banco de questões completo',
    'Métricas avançadas de desempenho',
];

interface ProductExtras {
    description: string;
    features: string[]; // só para access_all
}

const MarketplaceProducts: React.FC<MarketplaceProductsProps> = ({ onShowToast }) => {
    const { data: productsData, isLoading } = useAdminProducts();
    const invalidate = useInvalidate();

    const [activeSection, setActiveSection] = useState<EditSection>('access_all');
    const [editingId, setEditingId] = useState<string | null>(null);
    const [extras, setExtras] = useState<ProductExtras>({ description: '', features: [] });
    const [saving, setSaving] = useState(false);

    const products: Product[] = useMemo(
        () => productsData?.products?.filter((p: Product) => p.active) ?? [],
        [productsData]
    );

    const accessAllProducts = useMemo(() => products.filter(p => p.type === 'access_all'), [products]);
    const comboProducts = useMemo(() => products.filter(p => p.type === 'combo' || p.type === 'cycle'), [products]);

    const openEdit = (product: Product) => {
        setEditingId(product._id);
        setExtras({
            description: product.description || '',
            features: (product as any).features?.length
                ? (product as any).features
                : [...DEFAULT_FEATURES],
        });
    };

    const closeEdit = () => {
        setEditingId(null);
        setExtras({ description: '', features: [] });
    };

    const updateFeature = (i: number, val: string) => {
        const f = [...extras.features]; f[i] = val; setExtras({ ...extras, features: f });
    };

    const addFeature = () => setExtras({ ...extras, features: [...extras.features, 'Nova funcionalidade'] });
    const removeFeature = (i: number) => setExtras({ ...extras, features: extras.features.filter((_, idx) => idx !== i) });

    const handleSave = async (productId: string) => {
        setSaving(true);
        try {
            const token = localStorage.getItem('accessToken');
            const res = await fetch(`/api/admin/marketplace/products/${productId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    description: extras.description.trim(),
                    features: extras.features.filter(f => f.trim()),
                })
            });
            if (res.ok) {
                onShowToast('Produto atualizado!', 'success');
                closeEdit();
                invalidate.products();
            } else {
                const err = await res.json();
                onShowToast(err.message || 'Erro ao salvar', 'error');
            }
        } catch { onShowToast('Erro ao salvar', 'error'); }
        finally { setSaving(false); }
    };

    if (isLoading) return (
        <div className="mp-loading"><div className="mp-spinner" /><p>Carregando...</p></div>
    );

    return (
        <div className="mp-root">

            {/* ── HEADER ── */}
            <div className="mp-header">
                <div>
                    <h3>Editor do Marketplace</h3>
                    <p>Configure como os produtos aparecem na página de vendas</p>
                </div>
            </div>

            {/* ── SEÇÕES ── */}
            <div className="mp-section-tabs">
                <button
                    className={`mp-section-tab ${activeSection === 'access_all' ? 'active' : ''}`}
                    onClick={() => { setActiveSection('access_all'); closeEdit(); }}
                >
                    <Globe size={16} /> Acesso Total
                    <span className="mp-section-count">{accessAllProducts.length}</span>
                </button>
                <button
                    className={`mp-section-tab ${activeSection === 'combos' ? 'active' : ''}`}
                    onClick={() => { setActiveSection('combos'); closeEdit(); }}
                >
                    <Layers size={16} /> Combos & Ciclos
                    <span className="mp-section-count">{comboProducts.length}</span>
                </button>
            </div>

            {/* ── ACESSO TOTAL ── */}
            {activeSection === 'access_all' && (
                <div className="mp-list">
                    {accessAllProducts.length === 0 ? (
                        <div className="mp-empty">
                            <Globe size={40} strokeWidth={1.4} />
                            <p>Nenhum produto de Acesso Total ativo.</p>
                            <span>Crie um produto do tipo "Acesso Total" na aba Criar Produtos.</span>
                        </div>
                    ) : accessAllProducts.map(product => (
                        <div key={product._id} className="mp-product-row">
                            <div className="mp-product-row-header">
                                <div className="mp-product-row-info">
                                    <span className="mp-product-type-icon"><Globe size={16} /></span>
                                    <div>
                                        <strong>{product.title}</strong>
                                        <p>{product.description || <em>Sem descrição</em>}</p>
                                    </div>
                                </div>
                                {editingId === product._id ? (
                                    <button className="mp-btn-cancel-edit" onClick={closeEdit}>
                                        <X size={15} /> Cancelar
                                    </button>
                                ) : (
                                    <button className="mp-btn-edit" onClick={() => openEdit(product)}>
                                        Editar conteúdo
                                    </button>
                                )}
                            </div>

                            {editingId === product._id && (
                                <div className="mp-edit-panel">
                                    <p className="mp-edit-hint">
                                        Estes campos controlam o que aparece no card verde grande do marketplace.
                                    </p>

                                    <div className="mp-field-group">
                                        <label>Descrição do produto</label>
                                        <textarea
                                            value={extras.description}
                                            onChange={e => setExtras({ ...extras, description: e.target.value })}
                                            placeholder="Descreva o que está incluso neste plano..."
                                            className="mp-textarea"
                                            rows={3}
                                        />
                                    </div>

                                    <div className="mp-field-group">
                                        <div className="mp-features-header">
                                            <label>Funcionalidades incluídas (bullets)</label>
                                            <button className="mp-btn-add-feature" onClick={addFeature}>
                                                <Plus size={13} /> Adicionar
                                            </button>
                                        </div>
                                        <div className="mp-features-list">
                                            {extras.features.map((f, i) => (
                                                <div key={i} className="mp-feature-row">
                                                    <input
                                                        type="text"
                                                        value={f}
                                                        onChange={e => updateFeature(i, e.target.value)}
                                                        className="mp-input"
                                                        placeholder="Funcionalidade..."
                                                    />
                                                    <button className="mp-btn-remove-feature" onClick={() => removeFeature(i)}>×</button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="mp-edit-actions">
                                        <button className="mp-btn-save" onClick={() => handleSave(product._id)} disabled={saving}>
                                            {saving
                                                ? <><RefreshCw size={14} className="mp-spin" /> Salvando...</>
                                                : <><Save size={14} /> Salvar alterações</>
                                            }
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* ── COMBOS & CICLOS ── */}
            {activeSection === 'combos' && (
                <div className="mp-list">
                    {comboProducts.length === 0 ? (
                        <div className="mp-empty">
                            <Layers size={40} strokeWidth={1.4} />
                            <p>Nenhum combo ou ciclo ativo.</p>
                            <span>Crie produtos do tipo "Combo" ou "Campo de Estudo" na aba Criar Produtos.</span>
                        </div>
                    ) : comboProducts.map(product => (
                        <div key={product._id} className="mp-product-row">
                            <div className="mp-product-row-header">
                                <div className="mp-product-row-info">
                                    <span className="mp-product-type-icon">
                                        {product.type === 'cycle' ? <GraduationCap size={16} /> : <Layers size={16} />}
                                    </span>
                                    <div>
                                        <strong>{product.title}</strong>
                                        <p>{product.description || <em>Sem descrição</em>}</p>
                                    </div>
                                </div>
                                {editingId === product._id ? (
                                    <button className="mp-btn-cancel-edit" onClick={closeEdit}>
                                        <X size={15} /> Cancelar
                                    </button>
                                ) : (
                                    <button className="mp-btn-edit" onClick={() => openEdit(product)}>
                                        Editar conteúdo
                                    </button>
                                )}
                            </div>

                            {editingId === product._id && (
                                <div className="mp-edit-panel">
                                    <p className="mp-edit-hint">
                                        A descrição abaixo aparece no card full-width da listagem de combos.
                                    </p>

                                    <div className="mp-field-group">
                                        <label>Descrição do produto</label>
                                        <textarea
                                            value={extras.description}
                                            onChange={e => setExtras({ ...extras, description: e.target.value })}
                                            placeholder="Descreva os decks inclusos, público-alvo, diferenciais..."
                                            className="mp-textarea"
                                            rows={4}
                                        />
                                    </div>

                                    <div className="mp-edit-actions">
                                        <button className="mp-btn-save" onClick={() => handleSave(product._id)} disabled={saving}>
                                            {saving
                                                ? <><RefreshCw size={14} className="mp-spin" /> Salvando...</>
                                                : <><Save size={14} /> Salvar alterações</>
                                            }
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

        </div>
    );
};

export default MarketplaceProducts;